import React from "react";
import { useFormikContext } from "formik";
import { useTheme } from "styled-components";
import {
  StyledComposerTextArea,
  StyledInputField,
} from "../../Styles/StyledForms";

/**
 * The message field for the free-text composers — text, FAQ query and live
 * chat.
 *
 * The footer says "Press Enter to send · Shift+Enter for new line", but the
 * field was a single-line <input> with no key handling at all: Enter submitted
 * through the browser's native form submit, and Shift+Enter did the same,
 * because an <input> has nowhere to put a newline. This renders a textarea
 * instead and handles both keys, so the hint tells the truth.
 *
 * Off the redesign it is the original <input>, unchanged.
 */
// Matches StyledComposerTextArea's max-height: four 21.7px lines plus 18px of
// padding. If the two disagree, the box either scrolls early or overshoots.
const MAX_HEIGHT = 106;

interface Props {
  [otherProps: string]: any;
}

// The legacy path (isNewUi false) renders StyledInputField, an <input>, so the
// ref must admit both element types rather than promising a textarea always.
type ComposerEl = HTMLTextAreaElement | HTMLInputElement;

// forwardRef, not a plain function component: React strips `ref` before props
// reach one, so a caller's ref would silently never arrive. Live chat passes
// one to refocus the box when the agent stops typing and after a send.
const AppComposerField = React.forwardRef<ComposerEl, Props>(
  ({ ...otherProps }, forwardedRef) => {
    const theme: any = useTheme();
    const isNewUi = !!theme?.industry_style?.enabled;
    const { handleSubmit } = useFormikContext<any>();

    const attachRef = (el: ComposerEl | null) => {
      if (typeof forwardedRef === "function") forwardedRef(el);
      else if (forwardedRef) (forwardedRef as React.MutableRefObject<ComposerEl | null>).current = el;
    };

    if (!isNewUi) {
      return <StyledInputField ref={attachRef} {...otherProps} />;
    }

    // Grow with the content up to MAX_HEIGHT, then let it scroll. Reset first so
    // the box also shrinks back when text is deleted.
    const resize = (el: HTMLTextAreaElement | null) => {
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
    };

    return (
      <StyledComposerTextArea
        rows={1}
        {...otherProps}
        ref={(el: HTMLTextAreaElement | null) => {
          resize(el);
          attachRef(el);
        }}
        onInput={(e: React.FormEvent<HTMLTextAreaElement>) => {
          resize(e.currentTarget);
          otherProps?.onInput?.(e);
        }}
        onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
          if (e.key === "Enter" && !e.shiftKey) {
            // Enter sends. Without this the textarea would insert a newline and
            // nothing would ever be sent by keyboard.
            e.preventDefault();
            handleSubmit();
          }
          otherProps?.onKeyDown?.(e);
        }}
      />
    );
  }
);

AppComposerField.displayName = "AppComposerField";

export default AppComposerField;
